<template>
  <PageLoading v-if="isLoading"/>

  <div v-if="showExtensionsMessage">
    <div class="payjoy-top-banner">
      <img src="@/assets/icons/payjoy_logo.svg" alt="PayJoy"/>
    </div>
    <div class="extensions-message">
      <img v-if="extensionsMessageIcon" :src="extensionsMessageIcon" alt="Message icon"/>
      <div class="message">
        <h1>{{ extensionsMessageTitle }}</h1>
        <p>{{ extensionsMessageContent }}</p>
      </div>
      <div v-if="extensionsMessageButtonText" class="close-button-container">
        <button
          class="primary extension-continue-button"
          @click.stop="extensionsRetry"
        >
          {{ extensionsMessageButtonText }}
        </button>
      </div>
    </div>
  </div>

  <template v-if="!isLoading && !showExtensionsMessage">
    <div class="payjoy-top-banner">
      <img src="@/assets/icons/payjoy_logo.svg" alt="PayJoy"/>
    </div>
    <div class="providers-list">
      <h2 class="need-more-time-pay-label">
        {{ t("extensions-need-more-time-pay") }}
      </h2>
      <h1 class="listing-titles">
        {{ t("select-extension-type") }}
      </h1>
      <div v-if="extensionOptions.length">
        <ul>
          <li
            v-for="option in extensionOptions"
            :key="option.extensionType"
            :class="{ 'extension-option--selected': selectedExtensionType === option.extensionType, 'extension-option-denied': option.status === 'denied' }"
          >
            <CardItem
              :logo-src="getExtensionLogo(option)"
              logo-alt="Emergency extension"
              logo-size="40px"
              :title="getExtensionTitle(option)"
              :subtitle="getExtensionSubtitle(option)"
              :right-icon="getCheckboxIcon(option)"
              right-icon-alt="Select option"
              @click="selectExtensionType(option)"
            />
          </li>
        </ul>
      </div>
    </div>
    <div class="close-button-container">
      <button class="primary extension-continue-button" :disabled="!selectedExtensionType || extensionsBusy" @click="confirmExtensionType">
        {{t("continue") }}
      </button>
    </div>
  </template>

  <HighlightPopup
    :is-visible="showPopup"
    :popup-class="popupClass"
    :add-close-icon="true"
    :button-text="modalButtonText"
    button-color="#222926"
    :image-src="extensionModalImage"
    @close-popup="onClosePopup"
    @dismiss-popup="onDismissPopup"
    @dismiss-popup-close-btn="onDismissPopup"
  >
    <h1 class="text-black">
      {{ extensionModalTitle }}
    </h1>
    <MarkdownRenderer :content="extensionModalContent1" tag="p" class="extension-modal-content"/>
    <MarkdownRenderer :content="extensionModalContent2" tag="p" class="extension-modal-content"/>
  </HighlightPopup>

  <div v-if="showLoadingModal" class="loading-modal-overlay">
    <div class="loading-modal">
      <button class="popup-close-btn" @click="showLoadingModal = false">
        <img :src="closeSmallBlackIcon" alt="Close"/>
      </button>
      <img :src="loadingIconGreen" alt="Loading" class="loading-modal-icon"/>
      <p class="loading-modal-text">{{ t("loading") }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import {useRoute, useRouter} from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import router from "@/router";
import CardItem from "@/components/CardItem.vue";
import { AnalyticEventsNames } from "@/helpers/google-analytics.helpers";
import authHelper from "@/helpers/authHelper";
import { ActionLogTypeConst } from "@/types/IActionLogs";
import {IExtensionOption, IInitiateExtensionRequest, IInitiateExtensionResponse, ISelectExtensionRequest} from "@/types/IExtensions";
import {IAuthError} from "@/types/auth";
import {formatDateWeekday12h, formatDateWeekday12hParts, formatDayMonth} from "@/shared-helpers/date-time.helper";
import {IRolloutDeviceAssignmentRequest} from "@/types/IRollout";
import checkboxEmptyIcon from "@/assets/icons/checkbox_empty.svg";
import checkboxSelectedIcon from "@/assets/icons/checkbox_selected.svg";
import checkboxDisabledIcon from "@/assets/icons/checkbox_disabled.svg";
import extensionClockIcon from "@/assets/icons/extension_clock.svg";
import extensionClock2Icon from "@/assets/icons/extension_clock2.svg";
import extensionClock2DisabledIcon from "@/assets/icons/extension_clock2_disabled.svg";
import extensionWarningIcon from "@/assets/icons/extension_warning.svg";
import extensionErrorIcon from "@/assets/icons/extension_error.svg";
import loadingIconGreen from "@/assets/icons/loading_icon_green.svg";
import closeSmallBlackIcon from "@/assets/icons/close_small_black.svg";
import PageLoading from "@/components/PageLoading.vue";
import HighlightPopup from "@/components/HighlightPopup.vue";
import MarkdownRenderer from "@/components/MarkdownRenderer.vue";
import extensionInfoIcon from "@/assets/icons/extension_info.svg";
import {collapseTrailingPeriod} from "@/helpers/i18n.helper";

// Extension emergency types that skips the reason-selection screen when it carries no
// selectable reasons: the outage is not the customer's fault, so we grant the
// extension directly instead of routing to a reason screen with no options.
const PAYMENT_PROCESSOR_OUTAGE = "payment_processor_outage";
const LOCAL_DISRUPTIONS = "local_disruptions";

// other extension types
const CUSTOMER_VOLUNTARY_LONG = "customer_voluntary_long";
const ALIGN_TO_SALARY_PAYDATE = "align_to_salary_pay_date";

export default defineComponent({
  name: "ExtensionTypeView",
  components: {MarkdownRenderer, HighlightPopup, PageLoading, CardItem },
  emits: ["select-extension-type"],
  setup() {
    const { t, te, n, locale } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    // Extract device tag from route
    const deviceTag = computed<string>(() => route.params.deviceTag as string);
    // Initialize store with device tag
    if (deviceTag.value) {
      store.dispatch("payOptions/setDeviceTag", deviceTag.value);
      authHelper.setExtensionsDeviceTag(deviceTag.value);
    }

    const isLoading = ref(true);
    const showPopup = ref(false);
    const showLoadingModal = ref(false);

    const popupClass = ref("");
    const isGrantResultPopup = ref(false);
    const showExtensionsMessage = ref(false);
    const extensionsBusy = ref(false);
    const extensionsMessageTitle = ref("");
    const extensionsMessageContent = ref("");
    const extensionsMessageButtonText = ref("");
    const extensionsMessageIcon = ref("");

    const extensionModalImage = ref("");
    const extensionModalTitle = ref("");
    const extensionModalContent1 = ref("");
    const extensionModalContent2 = ref("");
    const modalButtonText = ref(t("extensions-confirm"));

    const extensionOptions = ref<IExtensionOption[]>([]);
    const extensionRequestData = ref<IInitiateExtensionResponse | null>(null);
    const selectedExtensionType = ref<string | null>(null);
    const selectedExtensionOption = ref<IExtensionOption | null>(null);

    const getCheckboxIcon = (option: IExtensionOption): string => {
      if (option.status === 'denied') return checkboxDisabledIcon;
      return selectedExtensionType.value === option.extensionType ? checkboxSelectedIcon : checkboxEmptyIcon;
    };

    return {
      t,
      locale,
      router,
      deviceTag,
      store,
      isLoading,
      showPopup,
      showLoadingModal,
      loadingIconGreen,
      closeSmallBlackIcon,
      extensionsBusy,
      extensionModalImage,
      extensionModalTitle,
      extensionModalContent1,
      extensionModalContent2,
      extensionOptions,
      extensionRequestData,
      selectedExtensionType,
      selectedExtensionOption,
      getCheckboxIcon,
      isGrantResultPopup,
      showExtensionsMessage,
      extensionsMessageTitle,
      extensionsMessageContent,
      extensionsMessageButtonText,
      extensionsMessageIcon,
      modalButtonText,
      popupClass,
    };
  },
  mounted() {
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page view
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          action: AnalyticEventsNames.OnSelectExtensionTypePage,
          actionType: ActionLogTypeConst.PAGE_VIEW,
        },
        this.$gtag,
      );
    }

    this.extensionRequest();
  },
  methods: {
    getExtensionLogo(option: IExtensionOption): string {
      // Emergency types (payment-processor outages, local disruptions) are not
      // the customer's fault, so surface a clock icon on the card to distinguish
      // them from other extension types.
      const emergencyTypes = [PAYMENT_PROCESSOR_OUTAGE, LOCAL_DISRUPTIONS];
      if (!emergencyTypes.includes(option.extensionType)) return "";
      return option.status === "denied" ? extensionClock2DisabledIcon : extensionClock2Icon;
    },
    getExtensionTitle(option: IExtensionOption): string {

      if(option.extensionType === ALIGN_TO_SALARY_PAYDATE) {
        return this.t("wait-until-payday");
      }
      else if(option.extensionType === PAYMENT_PROCESSOR_OUTAGE) {
        return this.t("payment-processor-outage-title");
      }
      else if(option.extensionType === LOCAL_DISRUPTIONS) {
        return this.t("local-disruptions-title");
      }
      return `${this.t(option.title)} ${this.t("hours")}`;
    },
    getExtensionSubtitle(option: IExtensionOption): string {

      if(option.reasonsKey === "It has been applied in this payment period") {
        return this.t("used-in-pay-period");
      }
      else if(option.reasonsKey === "Only available on days: 12, 13, 14, 27, 28 and 29 of month") {
        return this.t("extensions-payday-denied");
      }
      else if(option.extensionType === PAYMENT_PROCESSOR_OUTAGE) {
        return this.t("payment-processor-outage-subtitle");
      }
      else if(option.extensionType === LOCAL_DISRUPTIONS) {
        return this.t("local-disruptions-subtitle");
      }
      return this.t((option.subtitle ?? "").replaceAll("_", "-"));
    },
    async extensionRequest() {
      try {
        const deviceTag = this.store.state.payOptions.deviceInfo.deviceTag;
        if (!deviceTag) {
          console.error("Error initiating extension request: deviceTag is not available");
          this.isLoading = false;
          return;
        }

        const idemKey = `app-www-${deviceTag}-${Date.now()}`;
        const params: IInitiateExtensionRequest = {
          deviceTag: deviceTag,
          channel: "APP-WWW",
          idempotencyKey: idemKey,
          requesterEmail: `${deviceTag}@app.www`
        };

        const response = await authHelper.initiateExtensionRequest(params);
        if (response) {
          this.extensionRequestData = response;
          const requestId = this.extensionRequestData.id;
          const pollOptionsResponse = await authHelper.getAvailableExtensionOptions(requestId);

          if (pollOptionsResponse) {
            this.extensionOptions = pollOptionsResponse.extensionOptions.filter(
              (option) => option.reasonsKey !== "Not available in this country"
            );
            this.store.dispatch("payOptions/setRequestId", requestId);
            this.store.dispatch("payOptions/setIdempotencyKey", idemKey);
          }
          this.isLoading = false;
          return;
        }
      } catch (error) {
        console.error("Error initiating extension request:", error);
        this.showExtensionsMessage = true;

        if (error instanceof IAuthError && error.response?.error_code === "SERVICE_UNAVAILABLE") {
          this.extensionsMessageTitle = this.t("extensions-not-available");
          this.extensionsMessageContent = "";
          this.extensionsMessageButtonText = "";
          this.extensionsMessageIcon = extensionInfoIcon;
        } else {
          this.extensionsMessageTitle = this.t("extensions-failed-load");
          this.extensionsMessageContent = this.t("extensions-internal-problem");
          this.extensionsMessageButtonText = this.t("extensions-try-again");
          this.extensionsMessageIcon = extensionErrorIcon;
        }
      }
      this.isLoading = false;
    },
    selectExtensionType(extensionOption: IExtensionOption) {
      if (extensionOption.status === 'denied') return;
      this.selectedExtensionType = extensionOption.extensionType;
      this.selectedExtensionOption = extensionOption;

      this.store.dispatch("payOptions/setExtensionType", extensionOption.extensionType);
      this.store.dispatch("payOptions/setExtensionTime", extensionOption.extensionTime);
      this.store.dispatch("payOptions/setUiReasonKeys", extensionOption.uiReasonKeys);
      this.store.dispatch("payOptions/setUiReasons", extensionOption.uiReasons);
    },
    confirmExtensionType() {
      if(!this.selectedExtensionOption || !this.extensionRequestData) {
        return;
      }
      this.popupClass = "";
      this.showPopup = true;
      const today = new Date();
      const currentValidThrough = new Date(this.extensionRequestData.currentValidThrough);
      const validThrough = currentValidThrough < today ? today : currentValidThrough;
      validThrough.setHours(validThrough.getHours() + this.selectedExtensionOption.extensionTime);

      const { date: validThroughDate, time: validThroughTime } = formatDateWeekday12hParts(validThrough, this.locale, {includeWeekday: false});
      let paragraph1 = this.t("extensions-confirm-paragraph1", {date: validThroughDate, time: validThroughTime});
      paragraph1 = collapseTrailingPeriod(paragraph1);

      const timeLabel = `${this.selectedExtensionOption.title} ${this.t("hours")}`;
      this.extensionModalTitle = this.t("extensions-confirm-title", {time: timeLabel});

      const emergencyTypes = [PAYMENT_PROCESSOR_OUTAGE, LOCAL_DISRUPTIONS];
      if (emergencyTypes.includes(this.selectedExtensionOption.extensionType)) {
        this.extensionModalTitle = this.t("extensions-emergency-extension");
      }

      this.modalButtonText = this.t("extensions-confirm");
      this.extensionModalContent1 = paragraph1;
      this.extensionModalContent2 = this.t("extensions-confirm-paragraph2");

      // customize the modal content depending on the extension type
      if(this.selectedExtensionOption.extensionType === ALIGN_TO_SALARY_PAYDATE) {
          this.extensionModalContent2 = this.t("extensions-confirm-paragraph5");
      }
      else if(this.selectedExtensionOption.extensionType === CUSTOMER_VOLUNTARY_LONG
      && this.selectedExtensionOption.extensionData
      && this.selectedExtensionOption.extensionData.extensionsRemaining != null) {

        const count = this.selectedExtensionOption.extensionData.extensionsRemaining - 1;
        const total = this.selectedExtensionOption.extensionData.extensionsMax;
        const nextDate = this.selectedExtensionOption.extensionData.extensionsNextAvailableDate;

        if(count > 0) {
          // Only render paragraph3 when we have a total to interpolate; otherwise
          // keep the default paragraph2 rather than showing "{total}" empty.
          if(total != null) {
            this.extensionModalContent2 = this.t("extensions-confirm-paragraph3", {count: count, total: total});
          }
        }
        else if(nextDate != null) {
          const nextDateStr = formatDayMonth(nextDate, this.locale);
          this.extensionModalContent2 = this.t("extensions-confirm-paragraph4", {date: nextDateStr});
        }
      }
    },
    onDismissPopup() {
      this.showPopup = false;
      if (this.isGrantResultPopup) {
        window.location.reload();
      }
    },
    async onClosePopup(): Promise<void> {
      this.showPopup = false;

      if (this.isGrantResultPopup) {
        window.location.reload();
        return;
      }

      this.$emit("select-extension-type", this.selectedExtensionType);

      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            action: AnalyticEventsNames.OnSelectExtensionTypePage_SelectExtensionType,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
            data: {
              deviceTag: this.deviceTag,
              extensionType: this.selectedExtensionType
            },
          },
          this.$gtag,
        );
      }

      const uiReasonKeys = this.store.state.payOptions.extensions.uiReasonKeys;
      const extensionType = this.store.state.payOptions.extensions.extensionType;
      const hasNoReasons = !uiReasonKeys || uiReasonKeys.length === 0;

      // Payment-processor outages carries no reasons, so skip the reason screen and grant the extension directly
      if (hasNoReasons && extensionType === PAYMENT_PROCESSOR_OUTAGE) {
        this.showLoadingModal = true;
        this.extensionsBusy = true;
        await this.grantExtensionDirectly();
        this.extensionsBusy = false;
        return;
      }

      try {
        const deviceId = parseInt(this.extensionRequestData!.deviceId!);
        const rolloutRequest: IRolloutDeviceAssignmentRequest = {
          rollout_name: process.env.VUE_APP_EXTENSION_REASON_ROLLOUT_NAME || "",
          device_ids: [deviceId],
        };
        const rolloutResponse = await authHelper.getRolloutDeviceAssignment(rolloutRequest);

        if (Math.floor(Math.random() * 100) + 1 <= rolloutResponse.percentage) {
          this.showLoadingModal = true;
          this.extensionsBusy = true;
          await this.grantExtensionDirectly();
          this.extensionsBusy = false;
          return;
        }
      } catch (error) {
        console.error("Error checking rollout assignment:", error);
      }

      this.showLoadingModal = false;
      router.push({name: "ExtensionReasonView"});
    },
    async grantExtensionDirectly(): Promise<void> {
      const requestId = this.store.state.payOptions.extensions.requestId;
      const idemKey = this.store.state.payOptions.extensions.idempotencyKey;
      const extensionType = this.store.state.payOptions.extensions.extensionType;
      const extensionTime = this.store.state.payOptions.extensions.extensionTime;

      try {
        const params: ISelectExtensionRequest = {
          idempotencyKey: idemKey,
          extension_reason: "",
          extension_type: extensionType,
          extension_time: extensionTime,
        };
        const response = await authHelper.grantExtension(requestId!, params);
        if (response?.success && response?.status === "GRANTED") {
          this.popupClass = "extension-granted-popup";
          const newDate = formatDateWeekday12h(response.newValidThrough, this.locale);
          this.extensionModalImage = extensionClockIcon;
          this.extensionModalTitle = this.t("extensions-granted");
          this.extensionModalContent1 = collapseTrailingPeriod(this.t("extensions-new-payment-date", {date: newDate}));
          this.modalButtonText = this.t("extensions-confirm");
        } else {
          this.extensionModalImage = extensionWarningIcon;
          this.extensionModalTitle = this.t("extensions-request-error");
          this.extensionModalContent1 = this.t("extensions-request-failure");
          this.modalButtonText = this.t("extensions-try-again");
        }
      } catch (error) {
        this.extensionModalImage = extensionWarningIcon;
        this.extensionModalTitle = this.t("extensions-request-error");
        this.extensionModalContent1 = this.t("extensions-request-failure");
        this.modalButtonText = this.t("extensions-try-again");
      }
      this.extensionModalContent2 = "";
      this.showLoadingModal = false;
      this.isGrantResultPopup = true;
      this.showPopup = true;
    },
    extensionsRetry() {
      window.location.reload();
    }
  },
});
</script>

<style scoped>
.providers-list {
  padding: 16px 16px 128px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.providers-list h1 {
  color: #333;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.26px;
  margin: 0;
}

h3 {
  margin: 16px 0 0 0;
}

.providers-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.providers-list ul > li {
  position: relative;
}

.providers-list :deep(.card-item .bold) {
  font-weight: inherit;
}

.extension-option--selected :deep(.card-item) {
  border: 2px solid #222926;
}

.extension-option-denied :deep(.card-item) {
  background-color: #F1F0EE;
  border: 1px solid #C6C7BF;
}

.extension-option-denied :deep(.card-item) * {
  color: #74756F;
}

.message h1 {
  color: var(--Primary-Green-1);
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px; /* 120% */
}

.message h2 {
  color: var(--Typography-Text-2);
  font-feature-settings:
    "clig" off,
    "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}

.extension-continue-button {
  color: #fff;
  background-color: #222926;
}
.extension-continue-button:hover {
  background-color: #000 !important;
}
.extension-continue-button:disabled {
  background-color: #C6C7BF !important;
}
.need-more-time-pay-label {
  color: #74756F;
  margin: 5px 0;
  font-size: 1.1em;
  font-weight: 600;
}
.payjoy-top-banner {
  border-bottom: 2px solid #D4D4D4;
  padding: 15px;
}

@media (min-width: 768px) {
  .payjoy-top-banner {
    background-color: #fff;
    display: flex;
    justify-content: center;
  }
  .providers-list {
    max-width: 840px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  .close-button-container {
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
  .need-more-time-pay-label {
    padding-top: 1em;
  }
  .listing-titles {
    padding-bottom: 1em;
  }
}
.extension-modal-content {
  color:#3F4340;
}
.close-button-container {
  padding: 0 20px 30px;
}
.extensions-message {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  min-height: 200px;
  top: 50%;
  left: 10px;
  right: 10px;
  transform: translateY(-50%);
  max-width: 480px;
  margin: 0 auto;
  padding: 20px;
  border-radius: 30px;
  text-align: center;
}
.extensions-message p {
  margin: 1.5em 0;
}
.extensions-message :deep(.message) * {
  color:#3F4340;
}
.extensions-message img {
  width: 60px;
  height: 60px;
  margin: 0 auto;
}
.extensions-message .close-button-container {
  padding: 0;
  max-width: none;
  margin: 0;
}
.extensions-message .extension-continue-button {
  width: 100%;
}
:deep(.extension-granted-popup .popup-image) {
  margin: 45px auto -30px 0 !important;
}
:deep(.extension-granted-popup .message),
:deep(.extension-granted-popup .message *) {
  text-align: left !important;
}

/* On mobile, dock the continue button to the bottom of the viewport.
   Placed last so it wins the cascade over the base .close-button-container. */
@media (max-width: 767px) {
  .close-button-container {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px 20px;
  }
  .close-button-container .extension-continue-button {
    width: 100%;
  }
  /* Keep the retry button inside the centered error card, not docked */
  .extensions-message .close-button-container {
    position: static;
    padding: 0;
  }
  /* On mobile the confirmation modal text should always be left-aligned,
     overriding HighlightPopup's centered `.message` default. */
  :deep(.message),
  :deep(.message *) {
    text-align: left !important;
  }
}

/* On desktop the popup is a centered modal, so center its text and icon too
   (overrides the left-aligned granted-popup layout used on mobile). */
@media (min-width: 768px) {
  /* Present the popup as a centered modal instead of a full-width bottom
     sheet. Scoped to the extension views (previously lived in HighlightPopup,
     which leaked the styling to every other popup usage).
     The overlay's own `align-items: flex-end` can't be overridden from here
     because `:deep(.overlay)` targets the child's root element, which scoped
     `:deep()` cannot reach; `align-self: center` on the popup flex item
     overrides that alignment for just this item instead. */
  :deep(.popup) {
    align-self: center;
    max-width: min(84%, 1000px);
    border-radius: 16px;
    animation: fadeIn 0.2s ease-out;
  }
  :deep(.close-button-container) {
    padding-bottom: 12px;
  }
  :deep(.extension-granted-popup .popup-image) {
    margin: 45px auto -30px auto !important;
  }
  :deep(.extension-granted-popup .message),
  :deep(.extension-granted-popup .message *) {
    text-align: center !important;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Loading modal shown while the extension grant request is in flight.
   Follows the HighlightPopup pattern: full-width bottom sheet on mobile,
   centered modal on desktop. */
.loading-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
}
.loading-modal {
  position: relative;
  width: 100%;
  min-height: 250px;
  background: #fff;
  border-radius: 10px 10px 0 0;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  animation: loading-modal-slide-up 0.3s ease-out;
}
.loading-modal .popup-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: auto;
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}
.loading-modal .popup-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.08);
}
.loading-modal-icon {
  width: 48px;
  height: 48px;
  animation: loading-modal-spin 1s linear infinite;
}
.loading-modal-text {
  margin: 0;
  color: #3F4340;
  font-size: 16px;
  font-weight: 500;
}
@keyframes loading-modal-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loading-modal-slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@media (min-width: 768px) {
  .loading-modal-overlay {
    align-items: center;
  }
  .loading-modal {
    max-width: 400px;
    border-radius: 16px;
    animation: loading-modal-fade-in 0.2s ease-out;
  }
}
@keyframes loading-modal-fade-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
